import React from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, PackageCheck } from "lucide-react";
import Logo from "@/components/portal/Logo";
import FeedbackPrompt from "@/components/portal/FeedbackPrompt";

export default function Success() {
  const { state } = useLocation();
  const navigate = useNavigate();

  if (!state?.submission_id) return <Navigate to="/" replace />;

  const dt = new Date(state.submitted_at);
  const dateStr = dt.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
  const timeStr = dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      <header className="px-6 sm:px-10 pt-8">
        <div className="flex justify-center">
          <Logo size="sm" withMonogram={false} />
        </div>
      </header>

      <main className="flex-1 px-6 py-14 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-lg w-full"
        >
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 15 }}
              className="mx-auto h-16 w-16 rounded-full bg-maroon/[0.06] border border-maroon/20 flex items-center justify-center"
            >
              <PackageCheck className="text-maroon" size={30} strokeWidth={1.4} />
            </motion.div>
            <p className="overline mt-6">Confirmation</p>
            <h2 className="font-serif font-light text-5xl sm:text-6xl text-ink mt-3">Thank You</h2>
            <div className="divider-thin" />
            <p className="text-sm sm:text-base text-muted2 leading-relaxed max-w-md mx-auto">
              Your delivery details have been received successfully.
              <br />
              We will now prepare your parcel with the utmost care.
            </p>
          </div>

          <div className="mt-10 bg-white border border-line rounded-3xl p-6 sm:p-8">
            <Detail label="Invoice Number" value={state.invoice_number} testId="conf-invoice" />
            <Detail label="Submission ID" value={state.submission_id} testId="conf-submission-id" />
            <Detail label="Date" value={dateStr} testId="conf-date" />
            <Detail label="Time" value={timeStr} testId="conf-time" last />
          </div>

          <p
            data-testid="conf-whatsapp-note"
            className="text-center text-xs text-muted2 mt-6 leading-relaxed"
          >
            {state.whatsapp_scheduled
              ? "A confirmation has been sent to your WhatsApp."
              : "Our team will reach out with any updates."}
          </p>

          <FeedbackPrompt submissionId={state.submission_id} group={state.group_type} />

          <div className="mt-10 flex justify-center">
            <button
              data-testid="return-home-btn"
              onClick={() => navigate("/")}
              className="btn-outline-maroon inline-flex items-center gap-2"
            >
              <HomeIcon size={15} strokeWidth={1.5} />
              Return to Home
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

const Detail = ({ label, value, testId, last }) => (
  <div className={`flex items-center justify-between py-3 ${last ? "" : "border-b border-line/70"}`}>
    <span className="text-[11px] uppercase tracking-[0.24em] text-muted2">{label}</span>
    <span data-testid={testId} className="text-sm font-medium text-ink">{value}</span>
  </div>
);
